import {
  formatSearchValue,
  truncatePreview,
} from "./companyProfileSearch.js";

const CACHE_TTL_MS = 90_000;
const cache = new Map();

/** Cap searchable transcript text so huge calls don't freeze ranking. */
const MAX_TRANSCRIPT_CHARS = 80_000;

const flattenSearchableText = (value, depth = 0) => {
  if (value == null || depth > 4) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => flattenSearchableText(item, depth + 1)).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, val]) => `${key} ${flattenSearchableText(val, depth + 1)}`)
      .join(" ");
  }
  return "";
};

const formatCallDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
};

/**
 * Load CallRecordings for a company and build keyword-searchable palette items.
 * Searches Title, Participants, Source, Summary, takeaways, and Transcript.
 */
export async function loadCompanyCallRecordingsSearchIndex(
  company,
  { force = false } = {}
) {
  const companyId = company?.id;
  if (!companyId) {
    return { items: [], count: 0 };
  }

  const cacheKey = companyId;
  const cached = cache.get(cacheKey);
  if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.data;
  }

  const res = await fetch(
    `/api/callRecordings?companyId=${encodeURIComponent(companyId)}&includeTranscript=1`
  );
  const payload = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (payload.tableNotFound) {
      const empty = { items: [], count: 0 };
      cache.set(cacheKey, { at: Date.now(), data: empty });
      return empty;
    }
    throw new Error(payload.error || "Failed to load call recordings");
  }

  const records = Array.isArray(payload.records) ? payload.records : [];
  const companyName = company?.fields?.Name || "";

  const items = records
    .slice()
    .sort((a, b) => {
      const aDate = new Date(a.fields?.Date || 0).getTime();
      const bDate = new Date(b.fields?.Date || 0).getTime();
      return bDate - aDate;
    })
    .map((record) => {
      const fields = record.fields || {};
      const title = fields.Title || "Untitled call";
      const dateLabel = formatCallDate(fields.Date);
      const participants = String(fields.Participants || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
        .join(", ");
      const source = fields.Source ? String(fields.Source) : "";
      const summaryText = flattenSearchableText(fields.Summary);
      const transcriptRaw = fields.Transcript ? String(fields.Transcript) : "";
      const transcriptText =
        transcriptRaw.length > MAX_TRANSCRIPT_CHARS
          ? transcriptRaw.slice(0, MAX_TRANSCRIPT_CHARS)
          : transcriptRaw;

      const value = [
        title,
        dateLabel,
        participants,
        source,
        summaryText,
        transcriptText,
        companyName,
      ]
        .filter(Boolean)
        .join("\n");

      const previewBits = [
        dateLabel,
        participants ? truncatePreview(participants, 48) : null,
        summaryText
          ? truncatePreview(summaryText, 72)
          : transcriptText
            ? truncatePreview(transcriptText, 72)
            : null,
      ].filter(Boolean);

      return {
        id: `call-${record.id}`,
        resultKey: `call-${record.id}`,
        type: "call",
        sectionId: "company-call-recordings",
        recordingId: record.id,
        title,
        subtitle: previewBits.join(" · ") || "Call recording",
        value: formatSearchValue(value),
        keywords: [
          "call",
          "recording",
          "transcript",
          "summary",
          "takeaway",
          source,
          ...String(fields.Participants || "")
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean),
        ].filter(Boolean),
        category: "Call Recordings",
        hasTranscript: Boolean(transcriptRaw),
      };
    });

  const data = { items, count: items.length };
  cache.set(cacheKey, { at: Date.now(), data });
  return data;
}

export function invalidateCompanyCallRecordingsSearchIndex(companyId) {
  if (!companyId) {
    cache.clear();
    return;
  }
  cache.delete(companyId);
}
